/**
 * URL-attribute screening, shared by the JSX runtime's static-attribute
 * serializer (`renderAttr` in `jsx-runtime.ts`) and the fine-grained binding's
 * live-attribute writer (`setBoundAttr` in `bindings.ts`) — KF-297.
 *
 * A stored-XSS payload like `href={userInput}` with `javascript:alert(1)` is
 * dropped (attribute omitted from the string / removed from the live node)
 * rather than written. `raw(...)` / `SafeHtml` values bypass this — the
 * documented opt-out for legitimate cases (bookmarklet builders, values
 * sanitized by a separate trust layer).
 *
 * The screen is a scheme allow/deny check, not a full URL sanitizer, but it is
 * hardened against the obfuscations a real browser sees through:
 *
 *   - **Control-character / whitespace obfuscation of the scheme.** Browsers
 *     strip leading C0 controls + spaces and remove TAB/LF/CR from anywhere in
 *     a URL before resolving the scheme, so `java\tscript:`, `\x01javascript:`,
 *     and `javascript\x00:` all resolve to `javascript:`. We normalize the same
 *     way (strip every C0 control 0x00-0x1F + DEL, trim leading whitespace)
 *     BEFORE extracting the scheme, so none of those slip past.
 *
 *   - **The `javascript:` no-op placeholders are allowed.** `javascript:void(0)`
 *     and its handful of spellings are the placeholder-link idiom, not an
 *     attack; matching is against the whole normalized value so nothing can be
 *     appended to them. See `INERT_JAVASCRIPT_URLS`.
 *
 *   - **`data:` is subtype-specific.** `data:image/png` in `<img src>` is safe
 *     and common; `data:text/html` / `data:image/svg+xml` / `data:*xml` load as
 *     a document and run script (SVG `<script>`, HTML/XHTML `<script>`). We
 *     allowlist the inert media families (raster images, fonts, audio, video,
 *     plain text/css) and drop every other `data:` — so a novel dangerous
 *     subtype fails closed.
 */

import { devHooks } from '../dev-hooks.js';

/**
 * URL-bearing HTML/SVG attributes whose plain-string values are screened.
 * `data` covers `<object data>` (loads its target as a document, so
 * `data:text/html` there executes). Image-context URL attributes
 * (`srcset` / `poster` / `background` / `ping`) are intentionally NOT screened:
 * a `javascript:` / `data:` value in them does not execute as script in modern
 * browsers, so screening would add false positives without closing a real sink.
 */
const URL_ATTRS = new Set(['href', 'src', 'xlink:href', 'formaction', 'action', 'data']);

/** Schemes that execute script when a browser resolves them as a URL. */
const DANGEROUS_SCHEMES = new Set(['javascript', 'vbscript']);

/**
 * `javascript:` URLs that are provably no-ops — the placeholder-link idiom
 * (`<a href="javascript:void(0)">`) that a decade of jQuery-era markup, CMS
 * output, and ported templates is full of.
 *
 * Screening them out was a false positive with a bad failure mode: the `href`
 * is dropped, so the anchor stops being a link — no keyboard focus, no `:link`
 * styling, no pointer cursor — and in production that happens behind a
 * `console.warn` nobody reads.
 *
 * Allowing them costs nothing. The match is against the **whole** normalized
 * value, so no attacker-supplied suffix survives it: `javascript:void(0)` is in
 * the set and `javascript:void(0);alert(1)` is not. The bodies here evaluate to
 * `undefined` or to nothing at all, and they carry no interpolation point.
 *
 * The alternative — telling authors to write `raw('javascript:void(0)')` — is
 * strictly worse for security: it teaches the general-purpose escape hatch as
 * the answer to a benign case, and an author who has learned `raw()` for one
 * `href` will reach for it on the next one.
 *
 * Matching is exact after `normalizeUrl` + lowercase + trailing-whitespace trim.
 * Internal whitespace is NOT collapsed, so each accepted spelling is listed
 * explicitly and the set stays auditable at a glance.
 */
const INERT_JAVASCRIPT_URLS = new Set([
  'javascript:',
  'javascript:;',
  'javascript:void(0)',
  'javascript:void(0);',
  'javascript:void 0',
  'javascript:void 0;',
]);

/** Matches every C0 control character (0x00-0x1F) and DEL (0x7F). */
// eslint-disable-next-line no-control-regex -- deliberately matching C0 + DEL.
const CONTROL_CHARS = /[\u0000-\u001F\u007F]/g;

/**
 * Normalize a URL the way a browser does before scheme resolution: remove every
 * C0 control (0x00-0x1F) and DEL (0x7F) from anywhere — no legitimate URL
 * carries a raw control char (they must be percent-encoded), so this collapses
 * `java\tscript:` / `\x01javascript:` / `javascript\x00:` back to their real
 * scheme — then trim leading whitespace. Internal spaces are left intact: a
 * space is not a valid scheme character, so a browser treats `java script:` as
 * a relative URL, not the `javascript:` scheme, and we must not "repair" it
 * into a false positive.
 */
function normalizeUrl(value: string): string {
  return value.replace(CONTROL_CHARS, '').replace(/^\s+/, '');
}

/**
 * The lowercased URL scheme (text before the first `:`) of an ALREADY-NORMALIZED
 * value, or null if none.
 */
function schemeOf(normalized: string): string | null {
  const m = /^([a-zA-Z][a-zA-Z0-9+.-]*):/.exec(normalized);
  return m ? m[1].toLowerCase() : null;
}

/**
 * True if a `data:` URL (already normalized) is a script-executing document
 * type. Allowlists the inert media families and fails closed on everything else
 * — so `text/html`, `image/svg+xml`, XHTML/XML, and any unknown/novel subtype
 * are all treated as dangerous, while `data:image/png` / fonts / audio / video
 * / plain text pass.
 */
function isDangerousDataUrl(normalized: string): boolean {
  const media = /^data:([^;,]*)/.exec(normalized.toLowerCase())?.[1].trim() ?? '';
  if (media === '' || media === 'text/plain' || media === 'text/css') return false;
  if (media === 'image/svg+xml') return true; // SVG can carry <script>
  if (media.startsWith('image/')) return false;
  if (media.startsWith('font/') || media.startsWith('application/font')) return false;
  if (media.startsWith('audio/') || media.startsWith('video/')) return false;
  return true; // text/html, application/xhtml+xml, *xml, unknown → block
}

/** True if `value` is a dangerous URL for the URL-bearing attribute `name`. */
export function isDangerousUrlValue(name: string, value: string): boolean {
  if (!URL_ATTRS.has(name)) return false;
  // Normalized once here — every helper below reasons about the same string the
  // browser's scheme resolution would see.
  const normalized = normalizeUrl(value);
  const scheme = schemeOf(normalized);
  if (scheme === null) return false;
  if (DANGEROUS_SCHEMES.has(scheme)) {
    return !INERT_JAVASCRIPT_URLS.has(normalized.trimEnd().toLowerCase());
  }
  return scheme === 'data' && isDangerousDataUrl(normalized);
}

/**
 * The shared warning body for a dropped dangerous URL. Each caller prefixes
 * its own context (`JSX:` for the string serializer, `kerf binding:` for the
 * live-attribute writer).
 */
export function dangerousUrlWarning(name: string, value: string): string {
  return `dropped dangerous URL value for ${name}=${JSON.stringify(value.slice(0, 80))}. `
    + 'kerf blocks javascript:, vbscript:, and script-executing data: URLs '
    + '(text/html, image/svg+xml, xml) in href/src/data/formaction/action/xlink:href by default. '
    + 'Wrap in raw() if this is intentional (e.g. bookmarklets), or sanitize upstream.';
}

/**
 * Report a screened dangerous URL, with the dev-vs-prod split (KF-340). Both
 * call sites (the JSX string serializer's `renderAttr` and the live-attribute
 * writer's `setBoundAttr`) still DROP the attribute; this only chooses how the
 * screen surfaces the diagnostic:
 *
 *   - **DEV** (the consumer imported `kerfjs/dev`): THROW an `Error` with the
 *     diagnostic, failing loudly at the developer's desk so a mistyped/unsafe
 *     URL can't slip by unnoticed (nobody reads the console).
 *   - **PRODUCTION** (no dev entry installed): `console.warn` and let the
 *     caller drop — never crash a shipped app on attacker-influenced data.
 *
 * `context` is the caller's prefix (`JSX`/`kerf binding`); the composed message
 * is byte-identical to the pre-KF-340 warn text in production.
 */
export function reportDangerousUrl(context: string, name: string, value: string): void {
  const message = `${context}: ${dangerousUrlWarning(name, value)}`;
  devHooks.urlScreenThrow?.(message);
  console.warn(message);
}
